/**
 * The app shell: AppBar, a resizable left drawer, and the current screen.
 *
 * The drawer behavior is taken from BEETS' Layout - drag the right edge to
 * resize, clamped and remembered - because a qualified name is long and how
 * much room the tree gets is a per-person preference.
 *
 * THE CONTENT OFFSET LIVES IN EXACTLY ONE PLACE: the Drawer root's width. It is
 * a flex sibling of `<main>`, so its width already reserves the space the
 * sidebar occupies, and `<main>` needs no margin of its own. It carried one -
 * `ml` of the same width - and the two offsets ADDED: at a 2000px viewport the
 * drawer sat over 0-300 and the content began at 600, leaving 300px of nothing
 * between them. It survived because both offsets are zero when the sidebar is
 * closed, so it only showed one way round.
 *
 * The transition moved here with the offset. It was `create('margin')` on
 * `<main>`, and a margin that no longer changes animates nothing - the content
 * would snap while the paper slid. It is suppressed mid-drag, because a resize
 * is a direct manipulation: 195ms of easing between the cursor and the edge it
 * is holding reads as lag, not as polish.
 */

import { useCallback, useState } from 'react'
import { Link as RouterLink, Outlet } from 'react-router-dom'
import AppBar from '@mui/material/AppBar'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import DarkModeIcon from '@mui/icons-material/DarkMode'
import Divider from '@mui/material/Divider'
import Drawer from '@mui/material/Drawer'
import IconButton from '@mui/material/IconButton'
import LightModeIcon from '@mui/icons-material/LightMode'
import ListItemIcon from '@mui/material/ListItemIcon'
import LogoutIcon from '@mui/icons-material/Logout'
import Menu from '@mui/material/Menu'
import MenuIcon from '@mui/icons-material/Menu'
import MenuItem from '@mui/material/MenuItem'
import PersonIcon from '@mui/icons-material/Person'
import Toolbar from '@mui/material/Toolbar'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import useMediaQuery from '@mui/material/useMediaQuery'
import { useTheme } from '@mui/material/styles'
import BaselineBanner from './BaselineBanner'
import GitLabNotice from './GitLabNotice'
import ProjectPicker from './ProjectPicker'
import Sidebar from './Sidebar'
import { BUILD_LABEL_CLEARANCE } from './versionLabel'
import SuggestionBox from './SuggestionBox'
import WorkPicker from './WorkPicker'
import { signInUrl } from '../../api'
import { usePrincipal } from '../../state/AppData'
import { useProject } from '../../state/Project'
import { useThemeMode } from '../../state/ThemeMode'

const DEFAULT_WIDTH = 300
const MIN_WIDTH = 220
const MAX_WIDTH = 600
const WIDTH_KEY = 'best-sidebar-width'
const OPEN_KEY = 'best-sidebar-open'

function readNumber(key: string, fallback: number, min: number, max: number): number {
  try {
    const stored = parseInt(localStorage.getItem(key) ?? '', 10)
    if (Number.isNaN(stored)) return fallback
    return Math.min(max, Math.max(min, stored))
  } catch {
    return fallback
  }
}

function remember(key: string, value: string) {
  try {
    localStorage.setItem(key, value)
  } catch {
    // A layout preference is never worth failing over.
  }
}

export default function Layout() {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const principal = usePrincipal()
  const { current } = useProject()
  const { mode, toggle } = useThemeMode()

  const [open, setOpen] = useState(() => {
    try {
      return localStorage.getItem(OPEN_KEY) !== 'false'
    } catch {
      return true
    }
  })
  const [width, setWidth] = useState(() =>
    readNumber(WIDTH_KEY, DEFAULT_WIDTH, MIN_WIDTH, MAX_WIDTH),
  )
  const [mobileOpen, setMobileOpen] = useState(false)
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null)
  const [dragging, setDragging] = useState(false)

  const startResize = useCallback((event: React.MouseEvent) => {
    event.preventDefault()
    setDragging(true)
    const onMove = (moved: MouseEvent) => {
      // The drawer is docked to the left edge, so the new width is simply the
      // cursor's x position, clamped.
      setWidth(Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, moved.clientX)))
    }
    const onUp = () => {
      setDragging(false)
      document.body.style.userSelect = ''
      document.body.style.cursor = ''
      window.removeEventListener('mousemove', onMove)
      window.removeEventListener('mouseup', onUp)
      // Functional update, so the value stored is the final one rather than
      // the one captured when the drag began.
      setWidth((final) => {
        remember(WIDTH_KEY, String(final))
        return final
      })
    }
    document.body.style.userSelect = 'none'
    document.body.style.cursor = 'col-resize'
    window.addEventListener('mousemove', onMove)
    window.addEventListener('mouseup', onUp)
  }, [])

  const toggleDrawer = () => {
    if (isMobile) {
      setMobileOpen((current) => !current)
      return
    }
    setOpen((current) => {
      remember(OPEN_KEY, String(!current))
      return !current
    })
  }

  const drawerOpen = isMobile ? mobileOpen : open

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <AppBar position="fixed" sx={{ zIndex: theme.zIndex.drawer + 1 }}>
        <Toolbar variant="dense">
          <IconButton
            edge="start"
            color="inherit"
            onClick={toggleDrawer}
            aria-label={drawerOpen ? 'Hide navigation' : 'Show navigation'}
            sx={{ mr: 1.5 }}
          >
            <MenuIcon />
          </IconButton>

          <Typography
            variant="h6"
            component={RouterLink}
            to="/"
            sx={{
              color: 'inherit',
              textDecoration: 'none',
              fontWeight: 700,
              letterSpacing: '0.06em',
              mr: 1,
            }}
          >
            BEST
          </Typography>
          <Typography
            variant="body2"
            sx={{ opacity: 0.75, display: { xs: 'none', lg: 'block' } }}
          >
            Baseline Echelon SysML Tool
          </Typography>

          <Box sx={{ flexGrow: 1 }} />

          <ProjectPicker />
          <WorkPicker />

          {/* Beside the theme toggle rather than on a screen of its own: the
              thing worth reporting is whatever is on screen when it happens,
              and a report you have to navigate away to file is one nobody
              files. It draws nothing when it could not work - see
              SuggestionBox. */}
          <SuggestionBox />

          <Tooltip title={mode === 'dark' ? 'Switch to light' : 'Switch to dark'}>
            <IconButton color="inherit" onClick={toggle} aria-label="Toggle color scheme">
              {mode === 'dark' ? <LightModeIcon /> : <DarkModeIcon />}
            </IconButton>
          </Tooltip>

          {principal.anonymous ? (
            <Button color="inherit" href={signInUrl()} startIcon={<PersonIcon />}>
              Sign in
            </Button>
          ) : (
            <>
              <Button
                color="inherit"
                onClick={(event) => setMenuAnchor(event.currentTarget)}
                startIcon={<PersonIcon />}
              >
                {principal.name}
                <Chip
                  label={principal.role}
                  size="small"
                  sx={{ ml: 1, height: 20, color: 'inherit', borderColor: 'currentColor' }}
                  variant="outlined"
                />
              </Button>
              <Menu
                anchorEl={menuAnchor}
                open={Boolean(menuAnchor)}
                onClose={() => setMenuAnchor(null)}
              >
                <MenuItem disabled>Signed in via {principal.method}</MenuItem>
                <Divider />
                <MenuItem component="a" href="/auth/logout">
                  <ListItemIcon>
                    <LogoutIcon fontSize="small" />
                  </ListItemIcon>
                  Sign out
                </MenuItem>
              </Menu>
            </>
          )}
        </Toolbar>
      </AppBar>

      <Drawer
        variant={isMobile ? 'temporary' : 'persistent'}
        open={drawerOpen}
        onClose={() => setMobileOpen(false)}
        ModalProps={{ keepMounted: true }}
        sx={{
          // The ONLY offset. `<main>` is a flex sibling and takes what is left,
          // so a margin there would be this same space counted twice.
          width: drawerOpen ? width : 0,
          flexShrink: 0,
          transition: dragging
            ? 'none'
            : theme.transitions.create('width', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.leavingScreen,
              }),
          '& .MuiDrawer-paper': { width, boxSizing: 'border-box' },
        }}
      >
        <Toolbar variant="dense" />
        {/* Padded at the foot so the last sidebar item scrolls clear of the
            build label fixed to the corner (see BuildLabel). */}
        <Box
          sx={{
            overflow: 'auto',
            position: 'relative',
            height: '100%',
            pb: BUILD_LABEL_CLEARANCE,
          }}
        >
          <Sidebar onNavigate={isMobile ? () => setMobileOpen(false) : undefined} />
        </Box>
        {!isMobile && (
          <Box
            onMouseDown={startResize}
            onDoubleClick={() => {
              setWidth(DEFAULT_WIDTH)
              remember(WIDTH_KEY, String(DEFAULT_WIDTH))
            }}
            role="separator"
            aria-label="Resize navigation"
            sx={{
              position: 'absolute',
              top: 0,
              right: 0,
              width: 6,
              height: '100%',
              cursor: 'col-resize',
              '&:hover': { bgcolor: 'primary.main', opacity: 0.3 },
            }}
          />
        )}
      </Drawer>

      <Box component="main" sx={{ flexGrow: 1, minWidth: 0 }}>
        <Toolbar variant="dense" />
        {/* Keyed by program, so switching remounts the screen and it refetches.
            Every screen loads in an effect on mount, so without this a swap
            would leave the previous program's rows on display. */}
        {/* Bottom padding of BUILD_LABEL_CLEARANCE, which is more than the
            label is tall, so it never sits over the last row of a screen. */}
        <Box
          key={current?.slug ?? 'single'}
          // pb is given at BOTH breakpoints: the responsive `p` above would
          // otherwise win at md and drop the clearance.
          sx={{ p: { xs: 2, md: 3 }, pb: { xs: BUILD_LABEL_CLEARANCE, md: BUILD_LABEL_CLEARANCE } }}
        >
          {/* Over every screen while a baseline is the view (issue #97). */}
          <BaselineBanner />
          <GitLabNotice />
          <Outlet />
        </Box>
      </Box>
    </Box>
  )
}
